import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from '../composants/SideBar/SideBar';
import UserInfo from '../composants/UserInfo/UserInfo';
import SuspensionModal from '../composants/SuspensionModal/SuspensionModal';
import RattachementModal from '../composants/RattachementModal/RattachementModal';
import PopupProfilIncomplet from '../composants/PopupProfilIncomplet/PopupProfilIncomplet';
import { useAuth } from '../contexts/AuthContext';
import { ReactComponent as BrandLogo } from '../assets/logo/logo-centralis-blanc.svg';
import Footer from '../composants/Footer/Footer';
import { CentralisChat } from '../composants/CentralisChat';
import { InsideLayoutContext } from './layoutContext';
import './MainLayout.css';

// TEST : navigation deplacee dans la navbar (menu au survol des infos user).
// Mettre a true pour revenir a la sidebar laterale (rien n'est supprime).
const SIDEBAR_ENABLED = false;

// `children` optionnel : permet de reutiliser le layout (navbar) autour d'un contenu
// arbitraire (ex. pages d'erreur quand l'utilisateur est connecte). Sans children -> Outlet.
const MainLayout = ({ children }: { children?: React.ReactNode }) => {
  const { user, logout, suspensionInfo, rattachementInfo } = useAuth();
  const location = useLocation();
  const isAccueil = location.pathname === '/';
  const isNoSidebar = isAccueil || ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'].includes(location.pathname);
  // Le footer global s'affiche partout SAUF sur les pages d'auth. Il apparait donc AUSSI sur l'accueil
  // (source unique = ce meme <Footer />), pour un pied de page identique a celui de l'espace connecte.
  const isAuthPage = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'].includes(location.pathname);
  // La sidebar est TOUJOURS repliee ; elle se deploie au survol (CSS) et redecale le contenu.
  const isSidebarCollapsed = true;
  const sidebarShown = SIDEBAR_ENABLED && !isNoSidebar;
  const topNavMode = !SIDEBAR_ENABLED && !isNoSidebar; // navbar pleine largeur, nav dans le header
  // Pages pleine largeur (banner header) : suivis + detail candidature (+ sa demo) -> exemptees des marges.
  const isSuivi = /^\/(commande|operation)-/.test(location.pathname)
    || /^\/publications\/ac27\/(candidatures\/|demo)/.test(location.pathname);
  const [unreadCommandes, setUnreadCommandes] = useState(0);

  // Changement de page (pathname) -> retour en haut de la page cible.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Permet à une sous-page de mettre à jour le compteur
  const handleNotificationUpdate = (count: number) => {
    setUnreadCommandes(count);
  };

  // Compte parque dans l'entite par defaut : on ne rend QUE l'ecran bloquant. Rendre la page derriere
  // declencherait ses appels API — tous refuses 403 par EnsureEntiteRattachee — et le gestionnaire
  // d'erreur de UserInfo deconnecterait l'utilisateur vers /login avec le message, sans modale.
  // La suspension prime (ecran de suspension existant) : un seul ecran a la fois.
  if (!!user && !suspensionInfo?.suspendu && rattachementInfo?.bloque) {
    return (
      <InsideLayoutContext.Provider value={true}>
        <RattachementModal userName={user.name} userEmail={user.email} onLogout={logout} />
      </InsideLayoutContext.Provider>
    );
  }

  return (
    <InsideLayoutContext.Provider value={true}>
    <div className={`app-layout${isNoSidebar ? ' app-layout--no-sidebar' : ''}${topNavMode ? ' app-layout--topnav' : ''}`}>
      {/* FRONT-19: Modal de suspension inévitable */}
      {suspensionInfo?.suspendu && (
        <SuspensionModal
          motif={suspensionInfo.motif}
          depuis={suspensionInfo.depuis}
          onLogout={logout}
        />
      )}
      {/* Popup profil incomplet — carte d'angle non bloquante (acheteurs + prestataires libres) */}
      {!!user && <PopupProfilIncomplet />}
      {sidebarShown && (
        <Sidebar
          unreadCommandes={unreadCommandes}
          isCollapsed={isSidebarCollapsed}
          onCollapse={() => {}}
        />
      )}
      {/* Logo de marque : tuile fixe en haut a gauche, immobile. */}
      {!isNoSidebar && (
        <a href="/" className="app-brand-logo" aria-label="Accueil"><BrandLogo /></a>
      )}
      {/* Assistant d'aide : monte uniquement pour les utilisateurs connectes. */}
      {!!user && <CentralisChat />}
      <div className="app-layout-main">
        <main className={`main-content${isNoSidebar ? ' main-content--full' : sidebarShown ? ' sidebar-collapsed' : ' main-content--topnav'}${!isNoSidebar ? ' has-header' : ''}${isSuivi && !isNoSidebar ? ' main-content--bleed' : ''}`}>
          {!isNoSidebar && <UserInfo topNav={topNavMode} />}
          {/* Le contenu de la route s’affiche ici (ou les children fournis directement). */}
          {children ?? <Outlet context={{ onNotificationUpdate: handleNotificationUpdate }} />}
        </main>
        {/* Footer global : identique partout, y compris l'accueil. Masque seulement sur les pages d'auth. */}
        {!isAuthPage && <Footer />}
      </div>
    </div>
    </InsideLayoutContext.Provider>
  );
};

export default MainLayout;
